import React, { useState, useEffect, useRef, useCallback } from 'react';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import * as pdfjs from 'pdfjs-dist';
import mammoth from 'mammoth';
import {
    Upload, FileText, Layers, BarChart2, Terminal,
    CheckCircle2, AlertCircle, Clock, RefreshCw, Trash2,
    ChevronDown, X, Search, Filter, Zap, Activity,
    BookOpen, FileUp, Cpu, TrendingUp, AlertTriangle,
    Info, Bug, FolderOpen, Plus, Loader2, Eye, Download,
    FileImage, FileType, Settings2, Sparkles, Binary, StickyNote
} from 'lucide-react';

// Reference the worker copied to /public — safe for CRA / Webpack
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

// ─── Supabase tables ─────────────────────────────────────────────────────────
const T = {
    courses: 'courses',
    subjects: 'subjects',
    nodes: 'knowledge_folders',
    chunks: 'knowledge_files',
};

// ─── Tab config ───────────────────────────────────────────────────────────────
const TABS = [
    { id: 'ingestion',   label: 'Advanced Ingestion',     icon: FileUp },
    { id: 'insights',    label: 'Conversation Insights',  icon: BarChart2 },
    { id: 'logs',        label: 'Process Logs',           icon: Terminal },
    { id: 'api',         label: 'API Usage',              icon: Activity },
];

// ─── Parsers ──────────────────────────────────────────────────────────────────

/**
 * Extracts raw text and structure from various file formats
 */
async function extractTextFromFile(file, addLog) {
    const extension = file.name.split('.').pop().toLowerCase();
    addLog('DEBUG', `Analyzing file format: ${extension}`, 'extractor');

    try {
        if (extension === 'pdf') {
            const arrayBuffer = await file.arrayBuffer();
            const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
            let fullText = '';
            
            addLog('INFO', `Extracting text from ${pdf.numPages} PDF pages…`, 'pdf-parser');
            
            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const content = await page.getTextContent();
                const strings = content.items.map(item => item.str);
                fullText += strings.join(' ') + '\n\n';
                if (i % 5 === 0) addLog('DEBUG', `Parsed ${i}/${pdf.numPages} pages…`, 'pdf-parser');
            }
            return fullText;
        } 
        
        if (extension === 'docx') {
            addLog('INFO', `Parsing Word document: ${file.name}…`, 'docx-parser');
            const arrayBuffer = await file.arrayBuffer();
            const result = await mammoth.extractRawText({ arrayBuffer });
            return result.value;
        }

        // Default: Text/Markdown
        addLog('DEBUG', `Using standard text reader for ${extension}`, 'text-parser');
        return await file.text();
    } catch (err) {
        throw new Error(`Extraction failed: ${err.message}`);
    }
}

function splitIntoChunks(text, chunkSize = 800) {
    const cleanText = text.replace(/\s+/g, ' ').trim();
    if (cleanText.length <= chunkSize) return [cleanText];

    const chunks = [];
    let start = 0;
    
    while (start < cleanText.length) {
        let end = start + chunkSize;
        if (end < cleanText.length) {
            const lastPeriod = cleanText.lastIndexOf('. ', end);
            if (lastPeriod > start + (chunkSize / 2)) {
                end = lastPeriod + 1;
            }
        }
        chunks.push(cleanText.substring(start, end).trim());
        start = end;
    }
    return chunks;
}

function extractTitle(chunk, index, fileName) {
    const lines = chunk.split('\n');
    const firstLine = lines[0].trim();
    if (firstLine.length > 5 && firstLine.length < 80) return firstLine;
    const baseName = fileName.replace(/\.[^/.]+$/, "");
    return `${baseName} (P${index + 1})`;
}

/**
 * Ensures a name is unique within a set of existing names by appending (n)
 */
function resolveUniqueName(baseName, existingNames) {
    if (!existingNames.has(baseName)) return { name: baseName, isDuplicate: false };
    
    let counter = 1;
    let finalName = `${baseName} (${counter})`;
    while (existingNames.has(finalName)) {
        counter++;
        finalName = `${baseName} (${counter})`;
    }
    return { name: finalName, isDuplicate: true };
}

// ─── Log level styling ────────────────────────────────────────────────────────
const LOG_LEVEL = {
    DEBUG:    { icon: Bug,           color: 'text-zinc-400',   bg: 'bg-zinc-500/10' },
    INFO:     { icon: Info,          color: 'text-blue-500',   bg: 'bg-blue-500/10' },
    SUCCESS:  { icon: CheckCircle2,  color: 'text-emerald-500',bg: 'bg-emerald-500/10' },
    WARNING:  { icon: AlertTriangle, color: 'text-amber-500',  bg: 'bg-amber-500/10' },
    ERROR:    { icon: AlertCircle,   color: 'text-red-500',    bg: 'bg-red-500/10' },
};

// ─── Mock insights data ───────────────────────────────────────────────────────
const MOCK_INSIGHTS = [
    { query: 'What chapters cover thermodynamics?', count: 14, last: '2 hours ago', sources: 3 },
    { query: 'Explain Newton\'s laws with examples', count: 9, last: '5 hours ago', sources: 2 },
    { query: 'Practice problems for integration by parts', count: 22, last: '1 day ago', sources: 4 },
    { query: 'Summary of organic chemistry reactions', count: 7, last: '2 days ago', sources: 6 },
    { query: 'Key concepts for upcoming midterm', count: 31, last: '3 days ago', sources: 5 },
];

const MOCK_API = {
    totalCalls: 2843,
    tokensUsed: 1_240_000,
    tokensLimit: 5_000_000,
    successRate: 98.6,
    avgLatency: '320ms',
    byEndpoint: [
        { name: '/api/chat', calls: 1943, tokens: 890000 },
        { name: '/api/ingest', calls: 612, tokens: 302000 },
        { name: '/api/search', calls: 288, tokens: 48000 },
    ],
};

const DocumentProcessingPanel = ({ 
    targetFolderId, 
    isModalMode, 
    onSuccess 
}) => {
    const [activeTab, setActiveTab] = useState('ingestion');
    const [logs, setLogs] = useState([]);
    const [showMobileSidebar, setShowMobileSidebar] = useState(false);
    const [files, setFiles] = useState([]);
    const [processing, setProcessing] = useState(false);
    const [extractionProgress, setExtractionProgress] = useState(0);
    const fileInputRef = useRef(null);
    const [dragging, setDragging] = useState(false);

    const addLog = useCallback((level, message, source = 'system') => {
        setLogs(prev => [{
            id: Date.now() + Math.random(),
            level,
            message,
            source,
            timestamp: new Date().toISOString(),
        }, ...prev].slice(0, 200));
    }, []);

    const handleFileSelect = (e) => {
        const selected = Array.from(e.target.files).filter(f =>
            ['pdf', 'docx', 'txt', 'md', 'mdx'].includes(f.name.split('.').pop().toLowerCase())
        );
        setFiles(prev => [...prev, ...selected.map(f => ({ file: f, id: Date.now() + Math.random() }))]);
        e.target.value = '';
    };

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setDragging(false);
        const dropped = Array.from(e.dataTransfer.files).filter(f =>
            ['pdf', 'docx', 'txt', 'md', 'mdx'].includes(f.name.split('.').pop().toLowerCase())
        );
        if (dropped.length === 0) { toast.error('Format not supported.'); return; }
        setFiles(prev => [...prev, ...dropped.map(f => ({ file: f, id: Date.now() + Math.random() }))]);
    }, []);

    const removeFile = (id) => setFiles(prev => prev.filter(f => f.id !== id));

    const processFiles = async () => {
        if (!targetFolderId) { toast.error('No target folder selected'); return; }
        if (files.length === 0) { toast.error('No files to process'); return; }

        const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
        const teamId = profile.team_id;

        if (!teamId) {
            toast.error('No team session found');
            return;
        }

        setProcessing(true);
        setExtractionProgress(0);

        try {
            for (let i = 0; i < files.length; i++) {
                const { file } = files[i];
                setExtractionProgress(((i) / files.length) * 100);

                addLog('INFO', `Converting: ${file.name}`, 'conversion');
                const rawText = await extractTextFromFile(file, addLog);

                // Create the markdown file record
                const { error: insertError } = await supabase
                    .from(T.chunks)
                    .insert({
                        name: file.name.replace(/\.[^/.]+$/, "") + ".md",
                        body: rawText,
                        folder_id: targetFolderId,
                        team_id: teamId,
                        status: 'pending'
                    });

                if (insertError) throw insertError;
                
                addLog('SUCCESS', `Created markdown for ${file.name}`, 'conversion');
                setExtractionProgress(((i + 1) / files.length) * 100);
            }

            setFiles([]);
            setProcessing(false);
            toast.success(`Successfully converted ${files.length} documents to Markdown`);
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error(err);
            toast.error(`Conversion failed: ${err.message}`);
            setProcessing(false);
        }
    };

    return (
        <div className="flex h-full overflow-hidden relative bg-white dark:bg-zinc-950">
            {/* Show sidebar only if not in modal mode or if target folder isn't set */}
            {!targetFolderId && (
                <div className="fixed md:relative inset-y-0 left-0 w-80 border-r border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-950/20 p-5 overflow-y-auto shrink-0 z-50">
                    <div className="flex items-center gap-2 mb-4">
                        <FolderOpen className="w-4 h-4 text-blue-500" />
                        <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Selection Sidebar</p>
                    </div>
                </div>
            )}

            <div className="flex-1 p-8 overflow-y-auto space-y-8">
                <div className="max-w-3xl mx-auto space-y-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-zinc-900 dark:text-white">Document Convert</h2>
                            <p className="text-xs text-zinc-500">Select documents to convert into Markdown files in your current folder.</p>
                        </div>
                        {processing && (
                            <div className="flex items-center gap-3 bg-blue-50 dark:bg-blue-900/20 px-4 py-2 rounded-xl text-blue-600 dark:text-blue-400">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span className="text-[10px] font-black uppercase tracking-widest">Processing {Math.round(extractionProgress)}%</span>
                            </div>
                        )}
                    </div>

                    <div
                        onDragOver={e => { e.preventDefault(); setDragging(true); }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={cn(
                            'border-2 border-dashed rounded-[32px] p-12 flex flex-col items-center justify-center text-center cursor-pointer transition-all',
                            dragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/10' : 'border-zinc-200 dark:border-zinc-800 hover:border-blue-400'
                        )}
                    >
                        <input ref={fileInputRef} type="file" multiple accept=".pdf,.docx,.txt,.md,.mdx" className="hidden" onChange={handleFileSelect} />
                        <div className="w-16 h-16 rounded-3xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                            <FileUp className="w-8 h-8 text-zinc-400" />
                        </div>
                        <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-1">Upload Documents</h3>
                        <p className="text-sm text-zinc-500">Drag and drop files here to begin conversion.</p>
                    </div>

                    {files.length > 0 && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {files.map(({ file, id }) => (
                                    <div key={id} className="flex items-center gap-4 bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-2xl px-5 py-4">
                                        <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-500 flex items-center justify-center shrink-0">
                                            <FileText className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[13px] font-bold text-zinc-800 dark:text-zinc-100 truncate">{file.name}</p>
                                            <span className="text-[10px] font-bold text-zinc-400 uppercase">{file.name.split('.').pop()}</span>
                                        </div>
                                        <button onClick={(e) => { e.stopPropagation(); removeFile(id); }} className="text-zinc-300 hover:text-red-400">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button
                                onClick={processFiles}
                                disabled={processing}
                                className="w-full py-4 rounded-2xl bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest shadow-xl shadow-blue-500/20 hover:bg-blue-700 active:scale-[0.98] transition-all disabled:opacity-50"
                            >
                                {processing ? 'Converting Documents...' : `Convert ${files.length} Files to Markdown`}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function InsightsTab() { return <div className="p-8"><h2 className="text-xl font-bold">Insights Hub</h2><p className="text-zinc-500 mt-2">Analysis of knowledge search trends across the organization.</p></div>; }
function LogsTab({ logs }) { return <div className="p-8 h-full bg-zinc-950 font-mono text-[11px] text-zinc-400 overflow-y-auto"><h2 className="text-white mb-4">System Log Stream</h2>{logs.length === 0 ? 'No events.' : logs.map(l => <div key={l.id} className="mb-1">[{l.timestamp.split('T')[1].split('.')[0]}] [{l.level}] {l.message}</div>)}</div>; }
function ApiUsageTab() { return <div className="p-8"><h2 className="text-xl font-bold">Compute Status</h2><p className="text-zinc-500 mt-2">Token usage and latency metrics for RAG operations.</p></div>; }

export default DocumentProcessingPanel;
